import React from 'react';
import { Field, change, type WrappedFieldProps } from 'redux-form';
import { ConfigProvider, Flex, Popconfirm, Segmented, theme as antdTheme } from 'antd';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import BrandResourcesEditor from 'admin/components/common/BrandResourcesEditor';
import {
	getStoryBrandResources,
	updateStoryBrandResources,
	copyBrandResources,
	areBrandResourcesEqual,
} from 'common/brand-kit/resources';
import { countAssetReferencingBlocks } from 'common/brand-kit/asset-usage';
import { countDesignPresetBindings } from 'common/design-tokens/preset-usage';
import { antdThemeDark } from 'admin/components/common/Theme';
import BrandKitSelect from 'admin/components/common/BrandKitSelect';
import DashboardButton from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import Tooltip from 'admin/components/common/Tooltip';
import t from 'utils/translate';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import { AUTO_STORY_DESIGN_MODES, DEFAULT_STORY_DESIGN_MODES, SYSTEM_DEFAULT_FONT_FAMILY } from 'common/design-tokens';
import { getBrandKitById, normalizeStoredBrandKit } from 'common/brand-kit/projection';
import { SYSTEM_FONTS } from 'common/constants';
import { canonicalFontFamily } from 'admin/components/common/AI/Chat/fonts/font-resolver';
import type { SettingsTabsGeneralProps } from '../types';
import css from './Tokens.scss';

const storyTokensTheme = {
	...antdThemeDark,
	algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm],
};

function ResourcesField({
	input,
	meta,
	...props
}: WrappedFieldProps & Omit<React.ComponentProps<typeof BrandResourcesEditor>, 'value' | 'onChange'>) {
	return (
		<BrandResourcesEditor
			{...props}
			value={getStoryBrandResources(input.value || {})}
			onChange={next => input.onChange(updateStoryBrandResources(input.value || {}, next))}
			disabled={props.disabled || meta.submitting}
		/>
	);
}

export default function Tokens(props: SettingsTabsGeneralProps) {
	const { fields, version, formInfo, formValues } = props;
	const activeOrganization = useAdminSelector(selectActiveOrganization);
	const organization = activeOrganization?.id === props.story.organizationId ? activeOrganization : undefined;
	const fonts = [
		{ fontFamily: SYSTEM_DEFAULT_FONT_FAMILY },
		...Object.values({
			...SYSTEM_FONTS,
			...formValues.storyVersions?.[version]?.settings.fonts,
		}).filter(Boolean),
	];
	const dispatch = useAdminDispatch();
	const selectedBrandKitId = formValues.storyVersions?.[version]?.settings.brandKitId ?? '';
	const selectedKit = getBrandKitById(organization?.metadata?.brandKits, selectedBrandKitId);
	const selectedBrandKit = selectedKit ? normalizeStoredBrandKit(selectedKit) : undefined;
	const defaultPlatform = formValues.storyVersions?.[version]?.data.mediaQuery.defaultPlatform;
	const currentSettings = formValues.storyVersions?.[version]?.settings;
	const currentResources = getStoryBrandResources(currentSettings);
	const currentDesign = currentResources.design;
	const currentDesignModes = formValues.storyVersions?.[version]?.settings.designModes;
	const themeActivation = currentDesignModes?.activation.theme;
	const defaultThemeModeId = currentDesign.modeGroups.find(group => group.id === 'theme')?.defaultModeId ?? 'light';
	const appearance =
		themeActivation?.type === 'fixed'
			? themeActivation.modeId
			: themeActivation?.type === 'media'
				? 'auto'
				: defaultThemeModeId;
	const designMatchesBrandKit =
		!!selectedBrandKit &&
		!!currentSettings?.design &&
		!!currentSettings.brandContext &&
		areBrandResourcesEqual(currentResources, selectedBrandKit, defaultPlatform);
	const resetDisabledReason = !selectedBrandKit
		? t('designTokens.resetNoBrandKit')
		: designMatchesBrandKit && themeActivation
			? t('designTokens.resetAlreadyMatches')
			: undefined;
	const reset = () => {
		if (!selectedBrandKit) return;
		const { design, links, guidelines } = copyBrandResources(selectedBrandKit, defaultPlatform);
		const defaultFontFamily = design.defaults.root.fontFamily.base;
		const organizationFonts = Object.entries(organization?.metadata?.fonts ?? {}).filter(
			([, font]) => canonicalFontFamily(font.fontFamily) === canonicalFontFamily(defaultFontFamily)
		);
		dispatch(
			change(formInfo.name, `storyVersions.${version}.settings`, {
				...currentSettings,
				design,
				brandContext: { links, guidelines },
				// Missing activation adopts Light; authored appearance remains Story-owned.
				...(!themeActivation
					? {
							designModes: {
								activation: {
									...(currentDesignModes?.activation ?? {}),
									theme: DEFAULT_STORY_DESIGN_MODES.activation.theme,
								},
							},
						}
					: {}),
				...(organizationFonts.length
					? { fonts: { ...currentSettings?.fonts, ...Object.fromEntries(organizationFonts) } }
					: {}),
			})
		);
	};
	const changeBrandKit = (brandKitId: string) => {
		dispatch(change(formInfo.name, fields.brandKitId.name(version), brandKitId || null));
	};
	const changeAppearance = (next: string | number) => {
		const modeId = String(next);
		dispatch(
			change(
				formInfo.name,
				fields.designModes.name(version),
				modeId === 'auto'
					? AUTO_STORY_DESIGN_MODES
					: {
							activation: {
								...(currentDesignModes?.activation ?? {}),
								theme: { type: 'fixed', modeId },
							},
						}
			)
		);
	};
	const resetButton = (
		<Popconfirm
			disabled={!!resetDisabledReason || formInfo.submitting || formInfo.resourceBusy}
			title="Replace Story brand resources?"
			description="Replaces modes, defaults, tokens, assets, links and guidelines, including local-only resources."
			onConfirm={reset}
		>
			<DashboardButton
				theme="dark"
				view="secondary-danger"
				color="danger"
				size="medium"
				disabled={formInfo.submitting || formInfo.resourceBusy || !!resetDisabledReason}
			>
				{t('designTokens.reset')}
			</DashboardButton>
		</Popconfirm>
	);
	return (
		<ConfigProvider theme={storyTokensTheme}>
			<div className={css.tokens}>
				<header className={css.heading}>
					<Text
						tag="h1"
						className={css.title}
						size={Text.size.subheading}
						weight={Text.weight.semibold}
						compact
					>
						Brand resources
					</Text>
					<Text
						className={css.description}
						size={Text.size.body}
						text="Design presets, tokens, assets, links and guidelines owned by this Story. Changes here do not affect the organization Brand Kit."
						compact
					/>
				</header>
				<section className={css.appearance} aria-labelledby="story-appearance-heading">
					<div className={css.appearanceCopy}>
						<Text
							tag="h2"
							id="story-appearance-heading"
							size={Text.size.label}
							weight={Text.weight.semibold}
							compact
						>
							Story appearance
						</Text>
						<Text
							id="story-appearance-description"
							className={css.description}
							size={Text.size.label}
							compact
						>
							{appearance === 'auto'
								? "Follows the viewer's system color-scheme preference."
								: `This Story uses ${appearance === 'dark' ? 'Dark' : 'Light'} appearance.`}
						</Text>
					</div>
					<Segmented
						className={css.appearanceControl}
						aria-label="Story appearance"
						aria-describedby="story-appearance-description"
						value={appearance}
						disabled={formInfo.submitting || formInfo.resourceBusy}
						options={[
							{ value: 'auto', label: 'Auto' },
							{ value: 'light', label: 'Light' },
							{ value: 'dark', label: 'Dark' },
						]}
						onChange={changeAppearance}
					/>
				</section>
				<Field
					name={`storyVersions.${version}.settings`}
					component={ResourcesField}
					mediaQuery={formValues.storyVersions?.[version]?.data.mediaQuery}
					fonts={fonts}
					fontContext="story"
					disabled={formInfo.submitting || formInfo.resourceBusy}
					onBusyChange={props.onResourceBusyChange}
					getAssetUsageCount={id =>
						countAssetReferencingBlocks(formValues.storyVersions?.[version]?.data, id)
					}
					getPresetUsageCount={id => {
						const currentVersion = formValues.storyVersions?.[version];
						return currentVersion ? countDesignPresetBindings(currentVersion, id) : 0;
					}}
					onUpload={async asset => {
						const result = await props.postGallery({ storyId: props.story.id, asset });
						return result.success && result.result?.asset ? result.result : new Error('Upload failed.');
					}}
					theme="dark"
				/>
				<Flex vertical gap="large" aria-labelledby="story-token-brand-kit-heading" style={{ marginBlock: 40 }}>
					<Flex vertical gap="large">
						<Text tag="h2" id="story-token-brand-kit-heading" size={Text.size.article}>
							Brand Kit
						</Text>
						<Text className={css.brandKitDescription} size={Text.size.label}>
							• The selected Brand Kit is a library source, not a live connection.
							<br />• Reset copies all resources from this kit. Story appearance and other settings stay
							unchanged.
						</Text>
					</Flex>
					<div className={css.brandKitRow}>
						<BrandKitSelect
							className={css.brandKitSelect}
							id="story-token-brand-kit"
							ariaLabel="Brand Kit context"
							theme="dark"
							kits={organization?.metadata?.brandKits ?? []}
							value={selectedBrandKitId}
							includeNoBrandKit
							disabled={formInfo.submitting || formInfo.resourceBusy}
							onChange={changeBrandKit}
							menuPlacement="top"
						/>
						{resetDisabledReason ? (
							<Tooltip isStatic stylePreset="dark-2" placement="top" content={resetDisabledReason}>
								{resetButton}
							</Tooltip>
						) : (
							resetButton
						)}
					</div>
				</Flex>
			</div>
		</ConfigProvider>
	);
}
